// V3 Gym Console — check-ins + subscriptions esclusivamente su /api/v2.
// Prova Priorità 3: una capability (cap_checkins + cap_subscriptions)
// consumata come dominio autonomo — la console non sa che il verticale
// è gym. Zero dipendenza da plugin_*.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../auth/session';

type Row = Record<string, any>;

const card: React.CSSProperties = { background: 'var(--sk-card, #fff)', borderRadius: 12, padding: 16, border: '1px solid var(--sk-border, #e5e7eb)' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--sk-border, #d1d5db)', fontSize: 14 };
const btn = (bg = '#111827'): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 8, background: bg, color: '#fff', border: 0, cursor: 'pointer', fontSize: 13 });

export function V3GymPage() {
  const [checkins, setCheckins] = useState<Row[]>([]);
  const [plans, setPlans] = useState<Row[]>([]);
  const [subs, setSubs] = useState<Row[]>([]);
  const [customers, setCustomers] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [checkinForm, setCheckinForm] = useState({ customer_profile_id: '', method: 'manual' });
  const [subForm, setSubForm] = useState({
    customer_profile_id: '', plan_id: '', start_date: new Date().toISOString().slice(0, 10),
  });

  const load = useCallback(async () => {
    try {
      const [ci, pl, su, cu] = await Promise.all([
        apiFetch('/api/v2/checkins'), apiFetch('/api/v2/subscription-plans'),
        apiFetch('/api/v2/subscriptions'), apiFetch('/api/v2/customers'),
      ]);
      setCheckins(await ci.json()); setPlans(await pl.json());
      setSubs(await su.json()); setCustomers(await cu.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const activeSubs = useMemo(
    () => subs.filter((s) => s.status === 'active' || s.status === 'frozen'),
    [subs],
  );

  const nameOf = (id: unknown) =>
    customers.find((c) => String(c.id) === String(id))?.display_name ?? '—';

  const checkin = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/checkins', {
        method: 'POST',
        body: JSON.stringify({
          customer_profile_id: Number(checkinForm.customer_profile_id),
          method: checkinForm.method,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setCheckinForm({ ...checkinForm, customer_profile_id: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore check-in');
    } finally {
      setSaving(false);
    }
  };

  const subscribe = async () => {
    setSaving(true); setError('');
    try {
      const plan = plans.find((p) => String(p.id) === subForm.plan_id);
      const start = new Date(subForm.start_date);
      const end = plan?.duration_days
        ? new Date(start.getTime() + Number(plan.duration_days) * 86400000).toISOString().slice(0, 10)
        : subForm.start_date;
      const res = await apiFetch('/api/v2/subscriptions', {
        method: 'POST',
        body: JSON.stringify({
          customer_profile_id: Number(subForm.customer_profile_id),
          plan_id: Number(subForm.plan_id),
          start_date: subForm.start_date,
          end_date: end,
          status: 'active',
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setSubForm({ ...subForm, customer_profile_id: '', plan_id: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore sottoscrizione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(300px, 380px) 1fr' }}>
      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Check-in membro</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <select style={input} value={checkinForm.customer_profile_id}
              onChange={(e) => setCheckinForm({ ...checkinForm, customer_profile_id: e.target.value })}>
              <option value="">Membro…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
            </select>
            <select style={input} value={checkinForm.method}
              onChange={(e) => setCheckinForm({ ...checkinForm, method: e.target.value })}>
              <option value="manual">Manuale</option>
              <option value="badge">Badge</option>
              <option value="qr">QR</option>
            </select>
            <button style={btn()} disabled={saving || !checkinForm.customer_profile_id} onClick={() => void checkin()}>
              {saving ? 'Salvataggio…' : 'Registra check-in'}
            </button>
          </div>
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Assegna abbonamento</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <select style={input} value={subForm.customer_profile_id}
              onChange={(e) => setSubForm({ ...subForm, customer_profile_id: e.target.value })}>
              <option value="">Membro…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
            </select>
            <select style={input} value={subForm.plan_id}
              onChange={(e) => setSubForm({ ...subForm, plan_id: e.target.value })}>
              <option value="">Piano…</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.duration_days}gg · €{p.price}</option>)}
            </select>
            <input style={input} type="date" value={subForm.start_date}
              onChange={(e) => setSubForm({ ...subForm, start_date: e.target.value })} />
            <button style={btn('#0d6efd')} disabled={saving || !subForm.customer_profile_id || !subForm.plan_id} onClick={() => void subscribe()}>
              {saving ? 'Salvataggio…' : 'Assegna piano'}
            </button>
          </div>
        </div>
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        <p className="sk-muted" style={{ fontSize: 12 }}>
          API: /api/v2 · {checkins.length} check-in · {subs.length} abbonamenti · {plans.length} piani · {customers.length} membri
        </p>
      </div>

      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Abbonamenti attivi ({activeSubs.length})</h4>
          {activeSubs.length === 0 && <p className="sk-muted">Nessun abbonamento attivo.</p>}
          {activeSubs.map((s) => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{s.customer_name || nameOf(s.customer_profile_id)}</strong> · {s.plan_name}</span>
              <span style={{ color: '#6b7280' }}>{String(s.end_date).slice(0, 10)} · {s.status}</span>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Check-in recenti</h4>
          {checkins.length === 0 && <p className="sk-muted">Nessun check-in.</p>}
          {checkins.slice(0, 30).map((c) => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{c.customer_name || nameOf(c.customer_profile_id)}</strong></span>
              <span style={{ color: '#6b7280' }}>{String(c.checked_at).slice(0, 16).replace('T', ' ')} · {c.method}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
